import { faDownload, faUpload, faUpLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HelpIcon from '@mui/icons-material/Help';
import { Box, Chip, Grid, IconButton, SxProps, Table, TableBody, TableCell, TableRow, Tooltip } from '@mui/material';
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack5';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerEntity } from 'src/api/useAnswerApi';
import { EnqueteInfoEntity, ErrorMessageEntity, ErrorReportEntity, useEnqueteStatusPageApi } from 'src/api/useEnqueteStatusPageApi';
import { StyledButton } from 'src/components/app-components/StyledButton';
import { StyledLoading } from 'src/components/app-components/StyledLoading';
import { StyledModal } from 'src/components/app-components/StyledModal';
import { FORM_TYPE, SENT_STATUS, SURVEY_TYPE } from 'src/constants';
import { NetworkAccessError } from 'src/errors/errors';
import useLocales from 'src/hooks/useLocales';
import { usePermission } from 'src/hooks/usePermission';
import { useUser } from 'src/hooks/useUser';
import { useAppSelector } from 'src/redux/store';
import { dateFormat, formatterDateTime } from 'src/utils/formatDateTime';
import { getOrganizationString } from 'src/utils/getOrganizationString';
import { useEnqueteStatusList } from '../../../../../hooks/useEnqueteStatusList';
import Action5ErrorModal from '../../../../../modals/error-report/Action5ErrorModal';
import ErrorReportContent from '../../../../../modals/error-report/ErrorReportModal';

interface Props {
  respondentRequestId: string | null;
  surveyDetailId: string;
  enqueteInfoData: EnqueteInfoEntity | null;
  refreshData: () => void;
}

export const EnqueteInfoForm: React.FC<Props> = (props) => {
  const { respondentRequestId, surveyDetailId, enqueteInfoData, refreshData } = props;
  const { currentLang } = useLocales();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const pageApi = useEnqueteStatusPageApi();
  const pageHooks = useEnqueteStatusList();
  const { enqueueSnackbar } = useSnackbar();
  const { userNameJpn, userNameEng } = useUser();

  const { isAdmin, isSurveyOwner, isAnswerReader, isViewerAdmin, allowWriteDivisions, isDivisionReadRep } = usePermission();

  const isLangJpn = currentLang.value === 'ja';
  const [isOpenErrorNotificationModal, setIsOpenErrorNotificationModal] = useState<boolean>(false);
  const [errorReport, setErrorReport] = useState<ErrorMessageEntity[] | []>([]);
  const [isAvailableAnswer, getIsAvailableAnswer] = useState<boolean>(true);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [isDataLinkageButtonDisabled, setIsDataLinkageButtonDisabled] = useState<boolean>(true);
  const [errorList, setErrorList] = useState([]);
  const [isOpenAction5ErrorDetailModal, setIsOpenAction5ErrorDetailModal] = useState<boolean>(false);
  const filteredData = useAppSelector((state) => state.enqueteStatus.filteredData);
  const [isImportDisabled, setIsImportDisabled] = useState(false);

  //-------------------------------------------------------------
  // 回答状況の取得
  //-------------------------------------------------------------
  const getRequestAnswerRatio = (data: EnqueteInfoEntity | null) => {
    if (!data) return '';
    return `${data.answeredCount !== null ? data.answeredCount : '0'} / ${data.requestedCount !== null ? data.requestedCount : '0'}`;
  };

  //-------------------------------------------------------------
  // 依頼期間情報を取得
  //-------------------------------------------------------------
  const getRequestTerm = (data: EnqueteInfoEntity | null) => {
    if (!data) return '';
    const openedAt = formatterDateTime(data.openedAt) as string;
    const closedAt = formatterDateTime(data.closedAt) as string;

    return `${openedAt} 〜 ${closedAt}`;
  };

  //-------------------------------------------------------------
  // 添付ファイルダウンロード
  //-------------------------------------------------------------
  const handleExportZip = async () => {
    if (respondentRequestId && surveyDetailId) {
      //エクスポート
      await pageApi.exportZip(respondentRequestId, surveyDetailId, currentLang.value, filteredData);
    }
  };

  //-------------------------------------------------------------
  // 回答結果をエクスポート
  //-------------------------------------------------------------
  const handleExportExcel = async () => {
    if (respondentRequestId) {
      setInProgress(true);

      //エクスポート
      await pageApi.exportAnswerResult(respondentRequestId, isLangJpn ? 'jpn' : 'eng', filteredData);

      setInProgress(false);
    }
  };

  //-------------------------------------------------------------
  // Data-Insightデータ連携
  //-------------------------------------------------------------
  const handleSentDataInsight = async () => {
    if (surveyDetailId) {
      try {
        //Data-Insightデータ連携
        await pageApi.sentDataInsight(surveyDetailId);
        //回答状況更新の為に再取得
        refreshData();
      } catch (error) {
        if (error instanceof NetworkAccessError) {
          enqueueSnackbar(t('apiExceptionMessage.useEnqueteStatusPageApi.failedDataInsightSent'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('message.exceptionFailedClientSide'), { variant: 'error' });
        }
      }
    }
  };

  //-------------------------------------------------------------
  // 回答をインポート
  //-------------------------------------------------------------
  const handleImportExcel = async (e: ChangeEvent<any>) => {
    //FormData定義
    let formData = new FormData();

    //ファイル情報が未定義であれば終了
    if (inputRef.current !== null && inputRef.current?.files?.length === 0 && inputRef.current?.files !== null) return;
    //Filesデータがあれば、FileDataに格納
    if (inputRef.current !== null && inputRef.current?.files !== null) {
      formData.append('file', inputRef.current?.files[0]);
    }

    //ユーザー名を格納
    formData.append('userNameJpn', userNameJpn);
    formData.append('userNameEng', userNameEng);

    //RespondentRequestIdがなければ中断、あればインポート
    if (respondentRequestId === null) return;
    setInProgress(true);

    //インポート
    const res: AnswerEntity | AxiosResponse<any> | undefined = await pageApi.importAnswer(respondentRequestId, formData);

    //エラー分岐
    const clientErrorPattern = /^4[0-9]{2}$/;
    const serverErrorPattern = /^5[0-9]{2}$/;
    if (res !== undefined && res.status !== undefined && res.status === 400) {
      const error: ErrorReportEntity = res.data;
      setErrorReport(error.message);
      setIsOpenErrorNotificationModal(true);
    } else if (res !== undefined && res.status !== undefined && res.status === 406) {
      enqueueSnackbar(t('message.exceptionInvalidId'), { variant: 'error' });
    } else if (res !== undefined && res.status !== undefined && clientErrorPattern.test(String(res.status))) {
      enqueueSnackbar(t('message.exceptionFailedClientSide'), { variant: 'error' });
    } else if (res !== undefined && res.status !== undefined && serverErrorPattern.test(String(res.status))) {
      enqueueSnackbar(t('message.exceptionFailedServerSide'), { variant: 'error' });
    } else {
      //グリッド情報更新
      pageHooks.incrementUpdateCounter();
      //エラーレスポンスリセット
      setErrorReport([]);
      //スナックバー通知
      enqueueSnackbar(t('errorReport.message.succeedImport'), { variant: 'success' });
      //回答状況更新の為に再取得
      refreshData();
    }

    setInProgress(false);
  };

  //-------------------------------------------------------------
  // ファイル選択ダイアログ表示
  //-------------------------------------------------------------
  const openDialog = () => {
    if (inputRef.current === null) return;

    inputRef.current.click();
  };

  //-------------------------------------------------------------
  // 回答が可能であるか判定
  //-------------------------------------------------------------
  const judgmentAvailableAnswer = async () => {
    if (!respondentRequestId) return;
    if (enqueteInfoData === null) return;

    //依頼期間が有効か（依頼期間が過ぎていないか）
    const currentDateTimeMilliSec = new Date().getTime();
    const openDateTimeMilliSec = new Date(enqueteInfoData.openedAt).getTime();
    const closeDateTimeMilliSec = new Date(enqueteInfoData.closedAt).getTime();
    const isAvailableStatus = openDateTimeMilliSec <= currentDateTimeMilliSec && currentDateTimeMilliSec <= closeDateTimeMilliSec;

    // アクション⑤インポート編集権限なし
    //・アンケート回答期限外
    //・ログインユーザーが本部極担当者　かつ　閲覧権限
    //・ロールが「bonsai-viewer-admin」
    const isImportDisabled = !isAvailableStatus || 
      (isDivisionReadRep && allowWriteDivisions.length === 0 && !isAdmin && !isSurveyOwner) || 
      (isViewerAdmin && !isAdmin && !isSurveyOwner);
    setIsImportDisabled(isImportDisabled);

    //回答することができるか判定
    getIsAvailableAnswer(isAvailableStatus || isSurveyOwner || isAdmin);

    // Data-Insightへ連携ボタンの可視制御判定
    setIsDataLinkageButtonDisabled(
      !!(isAdmin && enqueteInfoData?.sentStatus === SENT_STATUS.PROCESSING) ||
        (isSurveyOwner && (enqueteInfoData?.sentStatus === SENT_STATUS.PROCESSING || enqueteInfoData?.sentStatus === SENT_STATUS.SENT_FAILED)) ||
        (!isAdmin && !isSurveyOwner),
    );
  };

  //-------------------------------------------------------------
  // アクション⑤エクスポート
  //-------------------------------------------------------------
  const handleAction5Export = async () => {
    if (respondentRequestId) {
      setInProgress(true);

      //エクスポート
      await pageApi.exportAction5AnswerResult(respondentRequestId, currentLang.value, filteredData);

      setInProgress(false);
    }
  };

  const handleAction5Import = () => {
    fileInputRef.current?.click();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  //-------------------------------------------------------------
  // アクション⑤インポート
  //-------------------------------------------------------------
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !respondentRequestId) return;
    const result = await pageApi.uploadAction5AnswerResult(respondentRequestId, file, userNameJpn, userNameEng);
    setErrorList(result ?? []);
    // エラーが発生した場合、「エラーレポート詳細」ダイアログを表示する。
    if(result?.length > 0){
      handleErrorDetail();
    }
    //グリッド情報更新
    pageHooks.incrementUpdateCounter();
    
  };

  //-------------------------------------------------------------
  // エラーレポート詳細
  //-------------------------------------------------------------
  const handleErrorDetail = async () => {
    setIsOpenAction5ErrorDetailModal(true);
  };

  //-------------------------------------------------------------
  // useEffect
  //-------------------------------------------------------------
  useEffect(() => {
    judgmentAvailableAnswer();
  }, [enqueteInfoData, isSurveyOwner]); //eslint-disable-line

  //-------------------------------------------------------------
  // Styles
  //-------------------------------------------------------------
  const headerStyle: SxProps = {
    width: '19em',
  };
  return (
    <>
      <Grid container>
        <Grid item xs={6}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell variant="head" sx={headerStyle} children={t('enqueteStatus.pageHeader.surveyTitle')} />
                <TableCell children={isLangJpn ? enqueteInfoData?.titleJpn : enqueteInfoData?.titleEng} />
              </TableRow>
              <TableRow>
                <TableCell variant="head" sx={headerStyle} children={t('enqueteStatus.pageHeader.answerStatus')} />
                <TableCell children={getRequestAnswerRatio(enqueteInfoData)} />
              </TableRow>
              <TableRow>
                <TableCell variant="head" sx={headerStyle} children={t('enqueteStatus.pageHeader.requestingOrganization')} />
                <TableCell children={getOrganizationString(enqueteInfoData, isLangJpn)} />
              </TableRow>
              <TableRow>
                <TableCell variant="head" sx={headerStyle} children={t('enqueteStatus.pageHeader.surveyRequestTerm')} />
                <TableCell children={getRequestTerm(enqueteInfoData)} />
              </TableRow>
              <TableRow>
                <TableCell variant="head" sx={headerStyle}>
                  {t('enqueteStatus.enqueteIntegrationStatus')}
                  <Tooltip
                    title={t('enqueteStatus.tooltip.dataInsightLabelStatusExplanationButtonText')
                      .split('\n')
                      .map((text) => (
                        <Box key={text}>{text}</Box>
                      ))}>
                    <IconButton>
                      <HelpIcon color="primary" sx={{ pt: '0px', pb: '0px', cursor: 'pointer' }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ color: 'red' }}>
                  {enqueteInfoData?.sentStatus === SENT_STATUS.UN_SENT && <Chip label={t('enqueteStatus.sentStatus.unSent')} color="info" />}
                  {enqueteInfoData?.sentStatus === SENT_STATUS.PROCESSING && (
                    <Chip label={t('enqueteStatus.sentStatus.processing')} color="warning" />
                  )}
                  {enqueteInfoData?.sentStatus === SENT_STATUS.SENT && (
                    <Chip
                      label={
                        t('enqueteStatus.sentStatus.sent') +
                        '(' +
                        DateTime.fromISO(enqueteInfoData?.sentAt ?? String(new Date())).toFormat(dateFormat) +
                        ')'
                      }
                      color="success"
                    />
                  )}
                  {enqueteInfoData?.sentStatus === SENT_STATUS.SENT_FAILED && (
                    <>
                      <Chip label={t('enqueteStatus.sentStatus.sentFailed')} color="error" />
                      <Tooltip
                        title={t('enqueteStatus.tooltip.dataInsightLinkageStatusSescription')
                          .split('\n')
                          .map((text) => (
                            <Box key={text}>{text}</Box>
                          ))}>
                        <IconButton>
                          <HelpIcon color="primary" sx={{ pt: '0px', pb: '0px', cursor: 'pointer' }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
        <Grid item container xs={6} alignItems={'flex-end'} justifyContent={'flex-end'} sx={{ textAlign: 'right' }} direction="column" rowSpacing={0}>
          <Grid item>
            {enqueteInfoData?.formType === FORM_TYPE.COMPLIA && (
              <StyledButton onClick={handleExportZip} sx={{ marginTop: '10px', marginLeft: '5px' }}>
                {t('enqueteStatus.menu.fileExport')}
              </StyledButton>
            )}

            <StyledButton onClick={handleExportExcel} sx={{ marginTop: '10px', marginLeft: '5px' }} startIcon={<FontAwesomeIcon icon={faDownload} />}>
              {t('enqueteStatus.menu.excelExport')}
            </StyledButton>

            {enqueteInfoData?.surveyType === SURVEY_TYPE.SPECIAL &&
              (enqueteInfoData?.formType === FORM_TYPE.SPECIAL ||
                (enqueteInfoData?.formType === FORM_TYPE.OVERVIEW && (isSurveyOwner || isAdmin))) && (
                <>
                  <StyledButton
                    onClick={openDialog}
                    sx={{ marginLeft: '4px', marginTop: '10px' }}
                    isDisabled={!isAvailableAnswer || isAnswerReader}
                    startIcon={<FontAwesomeIcon icon={faUpload} />}>
                    {t('enqueteStatus.menu.excelImport')}
                    <input
                      type="file"
                      value=""
                      hidden
                      ref={inputRef}
                      onChange={handleImportExcel}
                      onClick={(e) => {
                        if (inputRef.current !== null && inputRef.current?.files !== null) {
                          inputRef.current.files = null;
                        }
                      }}
                      accept={'.xlsx'}
                    />
                  </StyledButton>
                  <StyledButton
                    onClick={() => setIsOpenErrorNotificationModal(true)}
                    sx={{ marginLeft: '4px', marginTop: '10px' }}
                    isDisabled={!isAvailableAnswer || isAnswerReader}>
                    {t('errorReport.title')}
                  </StyledButton>
                </>
              )}
            <Tooltip
              title={t('enqueteStatus.tooltip.dataInsightLinkageStatusExplanationButtonText')
                .split('\n')
                .map((text) => (
                  <Box key={text}>{text}</Box>
                ))}>
              <span>
                <StyledButton
                  onClick={handleSentDataInsight}
                  sx={{ marginTop: '10px', marginLeft: '5px' }}
                  isDisabled={isDataLinkageButtonDisabled}
                  startIcon={<FontAwesomeIcon icon={faUpLong} />}>
                  {t('button.dataLinkage')}
                </StyledButton>
              </span>
            </Tooltip>
          </Grid>
          {enqueteInfoData?.formType === FORM_TYPE.COMPLIA && (
            <Grid item>
              <StyledButton
                onClick={handleAction5Export}
                sx={{ marginTop: '10px', marginLeft: '5px' }}
                startIcon={<FontAwesomeIcon icon={faDownload} />}>
                {t('enqueteStatus.menu.action5Export')}
              </StyledButton>
              <>
                <StyledButton
                  onClick={handleAction5Import}
                  sx={{ marginTop: '10px', marginLeft: '5px' }}
                  isDisabled={isImportDisabled}
                  startIcon={<FontAwesomeIcon icon={faUpload} />}>
                  {t('enqueteStatus.menu.action5Import')}
                </StyledButton>
                <input ref={fileInputRef} hidden type="file" accept={'.xlsx'} onChange={handleFileChange} />
              </>
              <StyledButton onClick={handleErrorDetail} sx={{ marginTop: '10px', marginLeft: '5px' }} isDisabled={isImportDisabled}>
                {t('enqueteStatus.menu.errorDetail')}
              </StyledButton>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/** エラーレポートモーダル */}
      <StyledModal
        isOpen={isOpenErrorNotificationModal}
        onCloseFunc={() => setIsOpenErrorNotificationModal(false)}
        modalTitle={t('errorReport.title')}>
        <ErrorReportContent errorReport={errorReport} onCloseFunc={() => setIsOpenErrorNotificationModal(false)} />
      </StyledModal>

      {/* エラーレポート詳細 */}
      <StyledModal
        isOpen={isOpenAction5ErrorDetailModal}
        onCloseFunc={() => setIsOpenAction5ErrorDetailModal(false)}
        modalTitle={t('errorReport.title')}>
        <Action5ErrorModal errorList={errorList} onCloseFunc={() => setIsOpenAction5ErrorDetailModal(false)} />
      </StyledModal>

      {/** Excel処理ローディング */}
      <StyledLoading isOpen={inProgress} />
    </>
  );
};
